import { PlusCircle } from 'lucide-react'
import { type ButtonHTMLAttributes, forwardRef } from 'react'

import { Button } from '@/components/button/button'

import { cn } from '@/lib/utils'

import { SELECT_FILTER_OPTION_VALUE_MAP } from '../constants'
import { type SelectFilterProps } from '../select-filter'

export const FilterButton = forwardRef<
  HTMLButtonElement,
  ButtonHTMLAttributes<HTMLButtonElement> & {
    buttonText: string
    displayValues: string[]
    filterOption?: SelectFilterProps['filterOption']
  }
>((props, ref) => {
  const { className, displayValues, buttonText, filterOption = SELECT_FILTER_OPTION_VALUE_MAP.or, ...rest } = props

  const capitalizedCurrentSelectOption = filterOption.toUpperCase()

  return (
    <Button
      ref={ref}
      {...rest}
      variant="outline"
      size="sm"
      disabled={false}
      className={cn(
        'rounded-md border-dashed border-input',
        'flex items-center gap-2',
        'hover:bg-white',
        'pl-4 pr-4',
        displayValues.length > 0 && 'pr-2',
        className
      )}
    >
      <PlusCircle size={16} />

      <span>{buttonText}</span>

      {displayValues.length > 0 && (
        <div className={cn('flex items-center gap-2')}>
          <div className={cn('h-4 w-[1px] bg-border')} />
          <div className={cn('flex items-center')}>
            {displayValues.length > 3 ? (
              <SelectedOptionItem option={`${displayValues.length} selected(${capitalizedCurrentSelectOption})`} />
            ) : (
              displayValues.flatMap((option, i) =>
                i === displayValues.length - 1
                  ? [<SelectedOptionItem key={option} option={option} />]
                  : [
                      <SelectedOptionItem key={`${option}-item`} option={option} />,
                      <span
                        key={`${option}-select-option`}
                        className={cn('text-[10px] font-normal text-slate-500', 'px-1 pb-0.5 pt-[6px]')}
                      >
                        {capitalizedCurrentSelectOption}
                      </span>,
                    ]
              )
            )}
          </div>
        </div>
      )}
    </Button>
  )
})
FilterButton.displayName = 'FilterButton'

const SelectedOptionItem = (props: { option: string }) => {
  return <div className={cn('rounded px-1 py-0.5', 'bg-secondary text-sm font-normal')}>{props.option}</div>
}
